import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { nanoid } from '../utils/id';
import { downloadCollabradorExport } from '../utils/collabradorExport';

export interface Concept {
  id: string;
  label: string;
  votes: number;
  source: 'ai' | 'manual';
}

export interface Synonym {
  id: string;
  conceptId: string;
  label: string;
  votes: number;
  dismissed: boolean;
  source: 'ai' | 'lexical';
}

export type DomainStatus = 'unknown' | 'checking' | 'available' | 'taken' | 'error';

export interface AvailableDomain {
  domain: string;
  tld: string;
  registerURL: string;
}

export interface NameScores {
  conceptFit: number;
  memorability: number;
  pronounceability: number;
  uniqueness: number;
  trademarkRisk: number;
  domainAvailability: number;
  searchDiscoverability: number;
  emotionalResonance: number;
  extendability: number;
  visualAdaptability: number;
}

export interface NameEvaluation {
  scores: NameScores;
  totalScore: number;
  strengths: string[];
  risks: string[];
  notes: string;
}

export interface Mashup {
  id: string;
  label: string;
  votes: number;
  sources: Array<{ type: 'concept' | 'synonym'; id: string }>;
  domainStatus: DomainStatus;
  availableDomains: AvailableDomain[];
  evaluation?: NameEvaluation;
}

export interface NamingProject {
  id: string;
  name: string;
  brief: string;
  concepts: Concept[];
  synonyms: Synonym[];
  mashups: Mashup[];
  shortlisted: string[];
  selectedTlds: string[];
  selectedStyles: string[];
  showOnlyFavorited: boolean;
  hideUnavailable: boolean;
}

interface SessionState {
  projects: Record<string, NamingProject>;
  projectOrder: string[];
  activeProjectId: string;
  createProject: (name?: string) => string;
  renameProject: (projectId: string, name: string) => void;
  selectProject: (projectId: string) => void;
  setBrief: (brief: string) => void;
  replaceConcepts: (concepts: Array<Omit<Concept, 'id'>>) => void;
  appendConcepts: (concepts: Array<Omit<Concept, 'id'>>) => void;
  addConcept: (label: string, source?: Concept['source']) => void;
  upvoteConcept: (conceptId: string) => void;
  removeConcept: (conceptId: string) => void;
  addSynonyms: (synonyms: Array<Omit<Synonym, 'id' | 'votes' | 'dismissed'> & { votes?: number }>) => void;
  voteSynonym: (synonymId: string, delta: 1 | -1) => void;
  dismissSynonym: (synonymId: string) => void;
  addMashups: (mashups: Array<Omit<Mashup, 'id' | 'votes' | 'domainStatus' | 'evaluation'> & { votes?: number }>) => void;
  clearMashups: () => void;
  removeMashup: (mashupId: string) => void;
  voteMashup: (mashupId: string, delta: 1 | -1) => void;
  toggleShortlist: (mashupId: string) => void;
  setDomainStatus: (mashupId: string, status: DomainStatus) => void;
  setAvailableDomains: (mashupId: string, domains: AvailableDomain[]) => void;
  toggleTld: (tld: string) => void;
  toggleStyle: (style: string) => void;
  clearStyles: () => void;
  toggleShowOnlyFavorited: () => void;
  toggleHideUnavailable: () => void;
  recheckAllDomains: () => void;
  setMashupEvaluations: (evaluations: Array<{ name: string; evaluation: NameEvaluation }>) => void;
  exportProject: () => void;
  exportForCollabrador: () => void;
  importProject: (projectData: NamingProject) => void;
}

const storage =
  typeof globalThis !== 'undefined' && 'localStorage' in globalThis
    ? (globalThis as typeof globalThis & { localStorage: Storage }).localStorage
    : undefined;

const createEmptyProject = (name: string): NamingProject => ({
  id: nanoid(),
  name,
  brief: '',
  concepts: [],
  synonyms: [],
  mashups: [],
  shortlisted: [],
  selectedTlds: ['com'],
  selectedStyles: [],
  showOnlyFavorited: false,
  hideUnavailable: false
});

const ensureProject = (state: SessionState): SessionState => {
  if (state.projectOrder.length === 0) {
    const project = createEmptyProject('Untitled Project');
    return {
      ...state,
      projects: { [project.id]: project },
      projectOrder: [project.id],
      activeProjectId: project.id
    };
  }

  // Migrate old projects to new format
  const migratedProjects: Record<string, NamingProject> = {};
  Object.keys(state.projects).forEach((id) => {
    const project = state.projects[id];
    migratedProjects[id] = {
      ...project,
      selectedTlds: project.selectedTlds || ['com'],
      selectedStyles: project.selectedStyles || [],
      showOnlyFavorited: project.showOnlyFavorited || false,
      hideUnavailable: project.hideUnavailable || false,
      mashups: project.mashups.map((m) => ({
        ...m,
        availableDomains: m.availableDomains || []
      }))
    };
  });

  return {
    ...state,
    projects: migratedProjects
  };
};

const withActiveProject = (
  set: (updater: (state: SessionState) => SessionState) => void,
  updater: (project: NamingProject) => NamingProject
) => {
  set((state) => {
    state = ensureProject(state);
    const project = state.projects[state.activeProjectId];
    return {
      ...state,
      projects: {
        ...state.projects,
        [project.id]: updater(project)
      }
    };
  });
};

const dedupeByLabel = <T extends { label: string }>(items: T[]): T[] => {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = item.label.trim().toLowerCase();
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

export const useSessionStore = create<SessionState>()(
  persist(
    (set, get) => {
      const initialProject = createEmptyProject('Untitled Project');
      return {
        projects: { [initialProject.id]: initialProject },
        projectOrder: [initialProject.id],
        activeProjectId: initialProject.id,
        createProject: (name = 'Untitled Project') => {
          const project = createEmptyProject(name);
          set((state) => ({
            projects: { ...state.projects, [project.id]: project },
            projectOrder: [project.id, ...state.projectOrder],
            activeProjectId: project.id
          }));
          return project.id;
        },
        renameProject: (projectId, name) =>
          set((state) => {
            const project = state.projects[projectId];
            if (!project) {
              return state;
            }
            return {
              ...state,
              projects: {
                ...state.projects,
                [projectId]: { ...project, name }
              }
            };
          }),
        selectProject: (projectId) =>
          set((state) =>
            state.projects[projectId]
              ? { ...state, activeProjectId: projectId }
              : state
          ),
        setBrief: (brief) =>
          withActiveProject(set, (project) => ({ ...project, brief })),
        replaceConcepts: (concepts) =>
          withActiveProject(set, (project) => ({
            ...project,
            concepts: concepts.map((concept) => ({ ...concept, id: nanoid() })),
            synonyms: [],
            mashups: [],
            shortlisted: []
          })),
        appendConcepts: (concepts) =>
          withActiveProject(set, (project) => {
            const merged = dedupeByLabel([
              ...project.concepts,
              ...concepts.map((concept) => ({ ...concept, id: nanoid() }))
            ]);
            return { ...project, concepts: merged };
          }),
        addConcept: (label, source = 'manual') =>
          withActiveProject(set, (project) => ({
            ...project,
            concepts: [
              ...project.concepts,
              {
                id: nanoid(),
                label,
                votes: source === 'manual' ? 1 : 0,
                source
              }
            ]
          })),
        upvoteConcept: (conceptId) =>
          withActiveProject(set, (project) => ({
            ...project,
            concepts: project.concepts.map((concept) =>
              concept.id === conceptId
                ? { ...concept, votes: concept.votes + 1 }
                : concept
            )
          })),
        removeConcept: (conceptId) =>
          withActiveProject(set, (project) => ({
            ...project,
            concepts: project.concepts.filter((concept) => concept.id !== conceptId),
            synonyms: project.synonyms.filter((syn) => syn.conceptId !== conceptId),
            mashups: project.mashups.filter((mashup) =>
              mashup.sources.every((source) => source.id !== conceptId)
            ),
            shortlisted: project.shortlisted.filter((id) => id !== conceptId)
          })),
        addSynonyms: (synonyms) =>
          withActiveProject(set, (project) => ({
            ...project,
            synonyms: [
              ...project.synonyms,
              ...synonyms.map((synonym) => ({
                ...synonym,
                id: nanoid(),
                votes: synonym.votes ?? 0,
                dismissed: false
              }))
            ]
          })),
        voteSynonym: (synonymId, delta) =>
          withActiveProject(set, (project) => ({
            ...project,
            synonyms: project.synonyms.map((synonym) =>
              synonym.id === synonymId
                ? { ...synonym, votes: Math.max(0, synonym.votes + delta) }
                : synonym
            )
          })),
        dismissSynonym: (synonymId) =>
          withActiveProject(set, (project) => ({
            ...project,
            synonyms: project.synonyms.map((synonym) =>
              synonym.id === synonymId ? { ...synonym, dismissed: true } : synonym
            )
          })),
        addMashups: (mashups) =>
          withActiveProject(set, (project) => {
            const merged = dedupeByLabel([
              ...project.mashups,
              ...mashups.map((mashup) => ({
                ...mashup,
                id: nanoid(),
                votes: 0,
                domainStatus: 'unknown' as DomainStatus,
                availableDomains: []
              }))
            ]);
            return { ...project, mashups: merged };
          }),
        clearMashups: () =>
          withActiveProject(set, (project) => ({
            ...project,
            mashups: [],
            shortlisted: []
          })),
        removeMashup: (mashupId) =>
          withActiveProject(set, (project) => ({
            ...project,
            mashups: project.mashups.filter((m) => m.id !== mashupId),
            shortlisted: project.shortlisted.filter((id) => id !== mashupId)
          })),
        voteMashup: (mashupId, delta) =>
          withActiveProject(set, (project) => ({
            ...project,
            mashups: project.mashups.map((mashup) =>
              mashup.id === mashupId
                ? { ...mashup, votes: Math.max(0, mashup.votes + delta) }
                : mashup
            )
          })),
        toggleShortlist: (mashupId) =>
          withActiveProject(set, (project) => ({
            ...project,
            shortlisted: project.shortlisted.includes(mashupId)
              ? project.shortlisted.filter((id) => id !== mashupId)
              : [...project.shortlisted, mashupId]
          })),
        setDomainStatus: (mashupId, status) =>
          withActiveProject(set, (project) => ({
            ...project,
            mashups: project.mashups.map((mashup) =>
              mashup.id === mashupId ? { ...mashup, domainStatus: status } : mashup
            )
          })),
        setAvailableDomains: (mashupId, domains) =>
          withActiveProject(set, (project) => ({
            ...project,
            mashups: project.mashups.map((mashup) =>
              mashup.id === mashupId ? { ...mashup, availableDomains: domains, domainStatus: domains.length > 0 ? 'available' as DomainStatus : 'taken' as DomainStatus } : mashup
            )
          })),
        toggleTld: (tld) =>
          withActiveProject(set, (project) => ({
            ...project,
            selectedTlds: project.selectedTlds.includes(tld)
              ? project.selectedTlds.filter((t) => t !== tld)
              : [...project.selectedTlds, tld]
          })),
        toggleStyle: (style) =>
          withActiveProject(set, (project) => ({
            ...project,
            selectedStyles: project.selectedStyles.includes(style)
              ? project.selectedStyles.filter((s) => s !== style)
              : [...project.selectedStyles, style]
          })),
        clearStyles: () =>
          withActiveProject(set, (project) => ({
            ...project,
            selectedStyles: []
          })),
        toggleShowOnlyFavorited: () =>
          withActiveProject(set, (project) => ({
            ...project,
            showOnlyFavorited: !project.showOnlyFavorited
          })),
        toggleHideUnavailable: () =>
          withActiveProject(set, (project) => ({
            ...project,
            hideUnavailable: !project.hideUnavailable
          })),
        recheckAllDomains: () =>
          withActiveProject(set, (project) => ({
            ...project,
            mashups: project.mashups.map((m) => ({
              ...m,
              domainStatus: 'unknown' as DomainStatus,
              availableDomains: []
            }))
          })),
        setMashupEvaluations: (evaluations) =>
          withActiveProject(set, (project) => {
            const evaluationMap = new Map(
              evaluations.map((e) => [e.name.toLowerCase().trim(), e.evaluation])
            );
            return {
              ...project,
              mashups: project.mashups.map((mashup) => {
                const evaluation = evaluationMap.get(mashup.label.toLowerCase().trim());
                return evaluation ? { ...mashup, evaluation } : mashup;
              })
            };
          }),
        exportProject: () => {
          const state = get();
          const project = state.projects[state.activeProjectId];
          if (!project) return;

          const dataStr = JSON.stringify(project, null, 2);
          const blob = new Blob([dataStr], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${project.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.json`;
          link.click();
          URL.revokeObjectURL(url);
        },
        exportForCollabrador: () => {
          const state = get();
          const project = state.projects[state.activeProjectId];
          if (!project) return;

          downloadCollabradorExport(project);
        },
        importProject: (projectData: NamingProject) => {
          const newProject: NamingProject = {
            ...projectData,
            id: nanoid()
          };
          set((state) => ({
            projects: { ...state.projects, [newProject.id]: newProject },
            projectOrder: [newProject.id, ...state.projectOrder],
            activeProjectId: newProject.id
          }));
        }
      };
    },
    {
      name: 'namenym-projects',
      storage: storage ? createJSONStorage(() => storage) : undefined,
      partialize: (state) => ({
        projects: state.projects,
        projectOrder: state.projectOrder,
        activeProjectId: state.activeProjectId
      })
    }
  )
);

const activeProject = (state: SessionState) => state.projects[state.activeProjectId];

export const sessionSelectors = {
  activeProject,
  projects: (state: SessionState) => state.projectOrder.map((id) => state.projects[id]),
  activeProjectId: (state: SessionState) => state.activeProjectId,
  brief: (state: SessionState) => activeProject(state)?.brief ?? '',
  concepts: (state: SessionState) => activeProject(state)?.concepts ?? [],
  synonyms: (state: SessionState) => activeProject(state)?.synonyms ?? [],
  mashups: (state: SessionState) => activeProject(state)?.mashups ?? [],
  shortlisted: (state: SessionState) => activeProject(state)?.shortlisted ?? [],
  selectedTlds: (state: SessionState) => activeProject(state)?.selectedTlds ?? ['com'],
  selectedStyles: (state: SessionState) => activeProject(state)?.selectedStyles ?? [],
  showOnlyFavorited: (state: SessionState) => activeProject(state)?.showOnlyFavorited ?? false,
  hideUnavailable: (state: SessionState) => activeProject(state)?.hideUnavailable ?? false
};
